import { createTheme, Theme as MuiTheme, PaletteColorOptions, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import React, { FC } from 'react';
import {
  InpageBagButtonType,
  InpageBuyButtonType,
  InpageMembershipButtonType,
  InpageProfileButtonType,
} from '../types/dtc';

interface CustomPaletteColor {
  main: string;
  light?: string;
  dark?: string;
}

export interface CustomGetAppBanner {
  enabled?: boolean;
  backgroundColor?: string;
  align?: 'top' | 'bottom';
  alignOffset?: number;
  zIndex?: number;
}

declare module '@mui/material/styles' {
  interface Theme {
    dtc?: boolean;
    baseFontSize?: number;
    fontWeight?: number;
    componentStyleOverrides?: Record<string, any>;
    globalButtonRadius?: number;
    customConnectReward?: {
      backgroundColor?: string;
      textColor?: string;
      headerText?: string;
    };
    getAppBanner?: {
      iosSmartBanner?: boolean;
      iosCustomBanner?: CustomGetAppBanner;
      androidCustomBanner?: CustomGetAppBanner;
    };
    dtcGetApp?: {
      enabled?: boolean;
      appImage?: string;
      backgroundColor?: string;
    };
    customRewardPillColor?: {
      purchase?: string;
      survey?: string;
      content?: string;
      affiliate?: string;
      dailyDeal?: string;
      default?: string;
      purchaseText?: string;
      surveyText?: string;
      contentText?: string;
      affiliateText?: string;
      dailyDealText?: string;
      defaultText?: string;
      outOfStock?: string;
      outOfStockText?: string;
    };
    activeInpageCheckout?: boolean;
    dtcFonts?: {
      fontFamily: string;
      baseFontSize: number;
      fontFamilyLink: string;
      viewBagTextOffsetY?: number;
    };
    barFonts?: {
      fontFamily: string;
      baseFontSize: number;
      fontFamilyLink: string;
    };
    customTypography?: {
      fontFamily: string;
      baseFontSize: number;
      fontFamilyLink: string;
    };
    inpageCheckoutComponent?: {
      bagButton: InpageBagButtonType;
      bagButtonMobile: InpageBagButtonType;
      membershipButton: InpageMembershipButtonType;
      membershipButtonMobile: InpageMembershipButtonType;
      profileButton: InpageProfileButtonType;
      profileButtonMobile: InpageProfileButtonType;
      buyButton: InpageBuyButtonType;
      buyButtonMobile: InpageBuyButtonType;
    };
  }
  interface ThemeOptions {
    dtc?: boolean;
    baseFontSize?: number;
    fontWeight?: number;
    componentStyleOverrides?: Record<string, any>;
    globalButtonRadius?: number;
    activeInpageCheckout?: boolean;
    customConnectReward?: {
      backgroundColor?: string;
      textColor?: string;
      headerText?: string;
    };
    getAppBanner?: {
      iosSmartBanner?: boolean;
      iosCustomBanner?: CustomGetAppBanner;
      androidCustomBanner?: CustomGetAppBanner;
    };
    barFonts?: {
      fontFamily: string;
      baseFontSize: number;
      fontFamilyLink: string;
    };
    dtcFonts?: {
      fontFamily: string;
      baseFontSize: number;
      fontFamilyLink: string;
      viewBagTextOffsetY?: number;
    };
    customTypography?: {
      fontFamily: string;
      baseFontSize: number;
      fontFamilyLink: string;
    };
    dtcGetApp?: {
      enabled?: boolean;
      appImage?: string;
      backgroundColor?: string;
    };
    customRewardPillColor?: {
      purchase?: string;
      survey?: string;
      content?: string;
      affiliate?: string;
      dailyDeal?: string;
      default?: string;
      purchaseText?: string;
      surveyText?: string;
      contentText?: string;
      affiliateText?: string;
      dailyDealText?: string;
      defaultText?: string;
      outOfStock?: string;
      outOfStockText?: string;
    };
    inpageCheckoutComponent?: {
      bagButton: InpageBagButtonType;
      bagButtonMobile: InpageBagButtonType;
      membershipButton: InpageMembershipButtonType;
      membershipButtonMobile: InpageMembershipButtonType;
      profileButton: InpageProfileButtonType;
      profileButtonMobile: InpageProfileButtonType;
      buyButton: InpageBuyButtonType;
      buyButtonMobile: InpageBuyButtonType;
    };
  }
  interface Palette {
    tertiary?: CustomPaletteColor;
    headerColor?: CustomPaletteColor;
  }

  interface PaletteOptions {
    tertiary?: CustomPaletteColor;
    headerColor?: CustomPaletteColor;
  }
}

/**
 * Adds light and dark theme color options if they are missing
 *
 * https://mui.com/material-ui/customization/palette/#generate-tokens-using-augmentcolor-utility
 */
const getAugmentedColorOption = (theme: MuiTheme, colorOption?: PaletteColorOptions) => {
  if (colorOption && 'main' in colorOption) {
    return theme.palette.augmentColor({
      color: {
        main: colorOption.main,
        light: colorOption.light,
        dark: colorOption.dark,
      },
    });
  }
  return colorOption;
};

export const mergeThemeOptions =
  (innerThemeOptions?: ThemeOptions) =>
  (outerTheme?: MuiTheme): MuiTheme => {
    const theme = outerTheme?.palette ? outerTheme : createTheme();
    if (!innerThemeOptions) {
      return theme;
    }
    const primary = getAugmentedColorOption(theme, innerThemeOptions?.palette?.primary);
    const augmentedInnerTheme = innerThemeOptions && {
      ...innerThemeOptions,
      palette: { ...innerThemeOptions.palette, primary },
    };

    return createTheme(deepmerge(theme, augmentedInnerTheme));
  };

interface CustomThemeProviderProps {
  children: React.ReactNode;
  themeOptions: ThemeOptions | undefined;
}
export const CustomThemeProvider: FC<CustomThemeProviderProps> = (props) => {
  return <ThemeProvider theme={mergeThemeOptions(props.themeOptions)}>{props.children}</ThemeProvider>;
};
